import { useState, lazy, Suspense, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useToast } from "@/hooks/use-toast";
import { Phone, Mail, MapPin, Droplets, Eye, ArrowUpFromLine, Thermometer, ArrowRight, Check, Shield, Zap, Camera, Building2, Landmark, PanelTop, Map } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import ServiceCard from "@/components/cards/ServiceCard";
import SimulateurModal from "@/components/modals/SimulateurModal";

// Lazy load RappelModal to reduce initial bundle (contains Calendar/react-day-picker)
const RappelModal = lazy(() => import("@/components/modals/RappelModal"));
import TestimonialsCarousel from "@/components/testimonials/TestimonialsCarousel";
import PortfolioGallery from "@/components/portfolio/PortfolioGallery";
// Hero image in public folder for LCP optimization

// Service card images
import cardDemoussage from "@/assets/card-demoussage.png";
import cardInspectionToiture from "@/assets/card-inspection-toiture.png";
import cardNettoyageHauteur from "@/assets/card-nettoyage-hauteur.webp";
import cardThermographie from "@/assets/card-thermographie.webp";
import cardBardage from "@/assets/card-bardage.png";
import cardInspectionBatiments from "@/assets/card-inspection-batiments.webp";
import cardMonuments from "@/assets/card-monuments.png";
import cardCartographie from "@/assets/card-cartographie.png";

const Index = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [simulateurOpen, setSimulateurOpen] = useState(false);
  const [rappelOpen, setRappelOpen] = useState(false);

  // Handle success message after form redirection
  useEffect(() => {
    const success = searchParams.get("success");
    if (success === "contact") {
      toast({
        title: "Message envoyé !",
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
      setSearchParams({}, { replace: true });
    } else if (success === "rappel") {
      toast({
        title: "Demande de rappel envoyée !",
        description: "Nous vous rappellerons à la date et l'heure souhaitées.",
      });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const services = [{
    title: "Démoussage de toiture",
    description: "Nettoyage complet de votre toiture par drone, sans échafaudage et sans risque pour les tuiles.",
    icon: <Droplets className="h-7 w-7 text-primary" />,
    link: "/services/demoussage",
    image: cardDemoussage
  }, {
    title: "Inspection de toiture",
    description: "Inspection visuelle haute définition pour repérer tuiles cassées, mousses et zones à risque.",
    icon: <Eye className="h-7 w-7 text-primary" />,
    link: "/services/inspection",
    image: cardInspectionToiture
  }, {
    title: "Nettoyage en hauteur",
    description: "Nettoyage de façades, vitres, verrières et panneaux solaires par drone, sans échafaudage ni nacelle.",
    icon: <ArrowUpFromLine className="h-7 w-7 text-primary" />,
    link: "/services/nettoyage-hauteur",
    image: cardNettoyageHauteur
  }, {
    title: "Inspection thermographique",
    description: "Inspection thermique par drone de vos panneaux photovoltaïques pour détecter les cellules défaillantes et optimiser votre production.",
    icon: <Thermometer className="h-7 w-7 text-primary" />,
    link: "/services/inspection-thermographique",
    image: cardThermographie
  }, {
    title: "Nettoyage de bardage",
    description: "Nettoyage professionnel des bardages et façades par drone, sans nacelle ni échafaudage.",
    icon: <PanelTop className="h-7 w-7 text-primary" />,
    link: "/services/nettoyage-bardage",
    image: cardBardage
  }, {
    title: "Inspection de bâtiments",
    description: "Inspection complète de vos bâtiments par drone pour diagnostiquer l'état des structures.",
    icon: <Building2 className="h-7 w-7 text-primary" />,
    link: "/services/inspection-batiments",
    image: cardInspectionBatiments
  }, {
    title: "Démoussage de monuments",
    description: "Traitement délicat des monuments historiques et patrimoniaux par drone.",
    icon: <Landmark className="h-7 w-7 text-primary" />,
    link: "/services/demoussage-monuments",
    image: cardMonuments
  }, {
    title: "Cartographie par drone",
    description: "Cartographie et relevés topographiques précis pour vos projets de construction ou d'aménagement.",
    icon: <Map className="h-7 w-7 text-primary" />,
    link: "/services/cartographie-drone",
    image: cardCartographie
  }];
  return <Layout>
      <Helmet>
        <title>Normandy Drone | Démoussage et inspection de toiture par drone en Normandie</title>
        <meta name="description" content="Normandy Drone : démoussage, inspection de toiture et nettoyage de façades par drone en Seine-Maritime (76) et Eure (27). Intervention sans échafaudage à Rouen, Le Havre, Évreux." />
                <link rel="canonical" href="https://www.normandydrone.fr/" />
        <meta property="og:title" content="Normandy Drone | Démoussage et inspection de toiture par drone en Normandie" />
        <meta property="og:description" content="Normandy Drone : démoussage, inspection de toiture et nettoyage de façades par drone en Seine-Maritime (76) et Eure (27). Intervention sans échafaudage à Rouen, Le Havre, Évreux." />
        <meta property="og:url" content="https://www.normandydrone.fr/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Normandy Drone | Démoussage et inspection de toiture par drone en Normandie" />
        <meta name="twitter:description" content="Normandy Drone : démoussage, inspection de toiture et nettoyage de façades par drone en Seine-Maritime (76) et Eure (27). Intervention sans échafaudage à Rouen, Le Havre, Évreux." />
        <link rel="preload" as="image" href="/hero-home.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-screen">
        <div className="absolute inset-0">
          <img 
            src="/hero-home.jpg" 
            alt="Drone survolant les falaises d'Etretat en Normandie" 
            fetchPriority="high"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 hero-gradient-overlay" />

        <div className="container relative z-10 py-16 lg:py-20">
          {/* Hero Content */}
          <div className="pt-16 lg:pt-24 pb-8 lg:pb-12 max-w-3xl">
            <div className="animate-slide-up">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
                <span className="text-white">Normandy</span>{" "}
                <span className="text-primary">Drone</span>
              </h2>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Intervention par drone", "Sans échafaudage", "Normandie · 76 & 27"].map(badge => <span key={badge} className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30">
                    {badge}
                  </span>)}
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Démoussage, nettoyage et inspection de toiture{" "}
                <span className="text-gradient">par drone</span> en Normandie (Seine-Maritime & Eure)
              </h1>

              <p className="text-base lg:text-lg text-white/80 mb-8 max-w-xl">
                Interventions rapides, sécurisées et sans échafaudage à Rouen, Le Havre, Évreux et dans toute la
                Normandie (Seine-Maritime & Eure). Profitez d'une technologie innovante pour l'entretien de votre toiture.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button size="lg" onClick={() => setSimulateurOpen(true)}>
                  Simuler mon tarif
                </Button>
                <Button variant="outline-hero" size="lg" onClick={() => setRappelOpen(true)}>
                  Demander un rappel
                </Button>
              </div>
            </div>
          </div>

          {/* Advantage Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 pb-8">
            {/* Card 1: Technologie innovante */}
            <div className="glass-card-dark p-4 lg:p-6 hover:border-primary/50 hover:shadow-[0_0_30px_hsl(var(--primary)/0.2)] transition-all duration-300 animate-fade-in" style={{
            animationDelay: "0.1s"
          }}>
              <div className="h-10 w-10 lg:h-14 lg:w-14 rounded-xl bg-primary/20 flex items-center justify-center mb-3 lg:mb-4 border border-primary/30">
                <Camera className="h-5 w-5 lg:h-7 lg:w-7 text-primary" />
              </div>
              <p className="text-base lg:text-lg font-semibold text-white mb-1 lg:mb-2">Technologie innovante</p>
              <p className="text-2xl lg:text-3xl font-bold text-primary mb-3 lg:mb-4">Précision 4K</p>
              <ul className="space-y-1.5 lg:space-y-2">
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Caméras haute définition</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Imagerie thermique</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Accès zones inaccessibles</span>
                </li>
              </ul>
            </div>

            {/* Card 2: Sécurité maximale */}
            <div className="glass-card-dark p-4 lg:p-6 hover:border-primary/50 hover:shadow-[0_0_30px_hsl(var(--primary)/0.2)] transition-all duration-300 animate-fade-in" style={{
            animationDelay: "0.2s"
          }}>
              <div className="h-10 w-10 lg:h-14 lg:w-14 rounded-xl bg-primary/20 flex items-center justify-center mb-3 lg:mb-4 border border-primary/30">
                <Shield className="h-5 w-5 lg:h-7 lg:w-7 text-primary" />
              </div>
              <p className="text-base lg:text-lg font-semibold text-white mb-1 lg:mb-2">Sécurité maximale</p>
              <p className="text-2xl lg:text-3xl font-bold text-primary mb-3 lg:mb-4">Zéro risque</p>
              <ul className="space-y-1.5 lg:space-y-2">
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Aucun personnel sur le toit</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Sans échafaudage</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Assurance civile</span>
                </li>
              </ul>
            </div>

            {/* Card 3: Réactivité garantie */}
            <div className="glass-card-dark p-4 lg:p-6 hover:border-primary/50 hover:shadow-[0_0_30px_hsl(var(--primary)/0.2)] transition-all duration-300 animate-fade-in sm:col-span-2 lg:col-span-1" style={{
            animationDelay: "0.3s"
          }}>
              <div className="h-10 w-10 lg:h-14 lg:w-14 rounded-xl bg-primary/20 flex items-center justify-center mb-3 lg:mb-4 border border-primary/30">
                <Zap className="h-5 w-5 lg:h-7 lg:w-7 text-primary" />
              </div>
              <p className="text-base lg:text-lg font-semibold text-white mb-1 lg:mb-2">Réactivité garantie</p>
              <p className="text-2xl lg:text-3xl font-bold text-primary mb-3 lg:mb-4">Sous 48h</p>
              <ul className="space-y-1.5 lg:space-y-2">
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Devis en 24h</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Intervention rapide</span>
                </li>
                <li className="flex items-center gap-2 text-sm lg:text-base text-white/70">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>Seine-Maritime & Eure</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Text */}
      <section className="py-12">
        <div className="container">
          <div className="glass-card p-8 max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">
              Normandy Drone : expert en intervention par drone en Normandie
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Normandy Drone</strong> est votre entreprise spécialisée dans le{" "}
                <Link to="/services/demoussage" className="text-primary-link hover:underline font-medium">démoussage de toiture par drone</Link>,{" "}
                l'<Link to="/services/inspection" className="text-primary-link hover:underline font-medium">inspection de toiture</Link> et le{" "}
                <Link to="/services/nettoyage-bardage" className="text-primary-link hover:underline font-medium">nettoyage de façades et bardages</Link>{" "}
                en Seine-Maritime (76) et dans l'Eure (27). Grâce à la technologie drone, nous intervenons sans échafaudage, 
                plus rapidement et en toute sécurité sur les toits de Rouen, Le Havre, Évreux et toute la Normandie.
              </p>
              <p>
              Notre gamme de services couvre l'ensemble des besoins en entretien et diagnostic de bâtiments :{" "}
                <Link to="/services/demoussage" className="text-primary-link hover:underline font-medium">traitement anti-mousse</Link> pour tuiles, ardoises et zinc,{" "}
                <Link to="/services/inspection-thermographique" className="text-primary-link hover:underline font-medium">inspection thermographique de panneaux photovoltaïques</Link>{" "}
                pour vérifier la performance de vos installations solaires et détecter les cellules défaillantes, et{" "}
                <Link to="/services/nettoyage-hauteur" className="text-primary-link hover:underline font-medium">nettoyage en hauteur</Link>{" "}
                de façades vitrées, verrières et panneaux solaires.
              </p>
              <p>
                Nous accompagnons aussi bien les <strong className="text-foreground">particuliers</strong> que les{" "}
                <strong className="text-foreground">professionnels et collectivités</strong> avec des services dédiés :{" "}
                <Link to="/services/inspection-batiments" className="text-primary-link hover:underline font-medium">inspection de bâtiments et gouttières</Link>{" "}
                pour syndics et transactions immobilières,{" "}
                <Link to="/services/demoussage-monuments" className="text-primary-link hover:underline font-medium">démoussage de monuments historiques</Link>{" "}
                compatible ABF, et{" "}
                <Link to="/services/cartographie-drone" className="text-primary-link hover:underline font-medium">cartographie par drone</Link>{" "}
                pour les projets d'aménagement et suivi de chantier.
              </p>
              <p>
                Chaque intervention fait l'objet d'un <strong className="text-foreground">rapport détaillé avec photos HD</strong> et recommandations. 
                Devis gratuit sous 24h, intervention rapide et tarifs compétitifs. 
                Contactez-nous pour un diagnostic personnalisé de votre toiture ou bâtiment en Normandie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Nos services par drone</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Des solutions innovantes pour l'entretien et l'inspection de votre toiture, sans
              risque et en toute sécurité.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(service => <ServiceCard key={service.title} {...service} />)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel />

      {/* Portfolio */}
      <PortfolioGallery />

      {/* Zone Highlight */}
      <section className="py-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container">
          <div className="glass-card p-10 text-center max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Rouen, Le Havre, Évreux et toute la Normandie
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Normandy Drone intervient en Seine-Maritime (76) et dans l'Eure (27) : Rouen, Le Havre, Évreux, Dieppe, Vernon, Louviers et dans de nombreuses communes
              alentour.
            </p>
            <Button asChild>
              <Link to="/zone-intervention">
                Voir toutes les villes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="py-12">
        <div className="container">
          <div className="glass-card p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <a href="tel:0760268944" className="flex items-center gap-4 p-4 rounded-xl hover:bg-primary/5 transition-colors">
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Téléphone</p>
                  <p className="font-semibold text-foreground">07 60 26 89 44</p>
                </div>
              </a>

              <a href="mailto:normandydrone76@gmail.com" className="flex items-center gap-4 p-4 rounded-xl hover:bg-primary/5 transition-colors">
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">E-mail</p>
                  <p className="font-semibold text-foreground">normandydrone76@gmail.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl">
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ville principale</p>
                  <p className="font-semibold text-foreground">Rouen – Normandie (76)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SimulateurModal isOpen={simulateurOpen} onClose={() => setSimulateurOpen(false)} />
      <Suspense fallback={null}>
        <RappelModal isOpen={rappelOpen} onClose={() => setRappelOpen(false)} />
      </Suspense>
    </Layout>;
};
export default Index;